import { ADMIN_API_BASE_URL } from "./config";

interface ApiError {
	error: string;
	/** Code stable posé par l'API quand l'interface doit traduire le refus. */
	code?: string;
}

/**
 * Échec d'un appel à l'API d'administration, avec le code de réponse HTTP.
 *
 * Le code est conservé parce qu'une page doit pouvoir distinguer un accès
 * refusé d'une panne : dans le premier cas elle annonce que la donnée n'est
 * pas ouverte à cet administrateur, dans le second qu'elle est momentanément
 * indisponible. Confondre les deux ferait croire à une avarie là où il n'y a
 * qu'une permission manquante.
 */
export class AdminApiError extends Error {
	constructor(message: string, readonly status: number, readonly code?: string) {
		super(message);
		this.name = "AdminApiError";
	}
}

class AdminApiClient {
	private token: string | null = null;

	setToken(token: string | null): void {
		this.token = token;
	}

	private async request<T>(
		method: string,
		path: string,
		body?: unknown,
	): Promise<T> {
		if (!path.startsWith("/")) {
			throw new Error("Admin API path must start with /");
		}
		if (path === "/api" || path.startsWith("/api/")) {
			throw new Error("Admin API path must be relative to VITE_ADMIN_API_BASE_URL and must not start with /api");
		}

		const headers: Record<string, string> = {};

		// Content-Type uniquement s'il y a un corps : annoncer du JSON sans en
		// envoyer fait rejeter la requête par Fastify (FST_ERR_CTP_EMPTY_JSON_BODY)
		// avant même d'atteindre la route. C'est ce qui rendait inopérants la
		// restauration d'une traduction automatique et la suppression d'une langue,
		// toutes deux servies par une requête DELETE sans corps.
		const hasBody = body !== undefined && body !== null;
		if (hasBody) {
			headers["Content-Type"] = "application/json";
		}

		if (this.token) {
			headers["Authorization"] = `Bearer ${this.token}`;
		}

		const response = await fetch(`${ADMIN_API_BASE_URL}${path}`, {
			method,
			headers,
			body: hasBody ? JSON.stringify(body) : undefined,
			credentials: "include", // Include cookies for admin auth
		});

		if (!response.ok) {
			const error: ApiError = await response.json().catch(() => ({
				error: "Request failed",
			}));
			throw new AdminApiError(error.error || "Request failed", response.status, error.code);
		}

		return response.json();
	}

	get<T>(path: string): Promise<T> {
		return this.request<T>("GET", path);
	}

	post<T>(path: string, body?: unknown): Promise<T> {
		return this.request<T>("POST", path, body);
	}

	put<T>(path: string, body?: unknown): Promise<T> {
		return this.request<T>("PUT", path, body);
	}

	patch<T>(path: string, body?: unknown): Promise<T> {
		return this.request<T>("PATCH", path, body);
	}

	delete<T>(path: string): Promise<T> {
		return this.request<T>("DELETE", path);
	}
}

export const adminApi = new AdminApiClient();
